import Immutable from 'immutable';
import { UserTypes } from './UserType';
import { accountManagerModules, moduleIds } from './Module';
import dayjs from 'dayjs';
import {getCentroid} from "../map";
import timezoneBoxes from "../../assets/timezoneBoxes.json";
import {canArchiveV4Account, canEditV4Translations, canManageV4Billing, canManageV4Devices, canManageV4Locale, canUpdateV4Account, canUpdateV4Locale, canManageV4Subject, managesV4Account} from "../v4Permissions";
import {getV4BootScope, getV4SessionScope} from "../v4Session";

// Per tab: the stored scope is shared, so another tab may have changed it since this one booted.
const bootedV4Scope = () => {
	const booted = getV4BootScope();
	return booted !== undefined ? booted : getV4SessionScope();
};

const UserModel = Immutable.Record({
	id: 0,
	language: 'nl-NL',
	arcKey: null,
	languages: [],
	token: null,
	email: null,
	lastLogin: null,
	groupId: 4,
	accountName: '',
	accountType: 0,
	name: '',
	alertExpiration: 'never',
	userType: UserTypes.NORMAL,
	modules: [],
	version: '',
	notifyEmail: false,
	showNewVersion: false,
	termsOfUseVersion: null,
	privacyVersion: null,
	showNewsletterOptIn: false,
	limitDevices: null,
	limitAccounts: null,
	accountId: 0,
	mustUseScript: false,
	trialEnd: null,
	appId: null,
	timezone: '',
	twoFA: false,
	incidentManagementUrl: null,
	arcs: [],
	scheduledReportLimit: 3,
	v4AccountSettings: null,
	v4Native: false,
	v4Permissions: null,
	v4OwnerId: null,
	v4UserId: null,
	v4ArcId: null
});

export default class User extends UserModel {
	id: number;
	language: string;
	languages: string[];
	arcKey: string;
	token: string;
	email: string;
	lastLogin: string;
	groupId: number;
	accountName: string;
	accountType: number;
	alertExpiration: string;
	name: string;
	userType: string;
	version: string;
	modules: number[];
	notifyEmail: boolean;
	showNewVersion: boolean;
	termsOfUseVersion: string;
	privacyVersion: string;
	showNewsletterOptIn: boolean;
	incidentManagementUrl: string;
	limitDevices: number;
	limitAccounts: number;
	accountId: number;
	mustUseScript: boolean;
	trialEnd: number;
	appId: number;
	timezone: string;
	twoFA: boolean;
	arcs: string[];
	scheduledReportLimit: number;
	v4AccountSettings: object;
	v4Native: boolean;
	v4Permissions: object;
	v4OwnerId: string;
	v4ArcId: string;
	get locationMaxValidity() {
		return +(this.v4AccountSettings?.LOCATION_VALIDITY_EXPIRY || 0);
	}
	// Days an alarm may stay open before the platform closes it; 0 = never.
	get alarmAutoCloseDays() : number {
		return +(this.v4AccountSettings?.alarm_auto_close_interval_days || 0);
	}
	// V4-only account: V3-only features hide and entity creation defaults to V4.
	get isV4Only() : boolean {
		const v4only = this.v4AccountSettings?.v4only;
		return v4only === true || v4only === 'true';
	}
	// Synthetic session authenticated purely by a V4 token: every V3
	// REST/websocket path must stay silent for it.
	get isV4Native() : boolean {
		return this.get('v4Native') === true;
	}
	// Literal key duplicated from utils/v4Session.js (V4_SCOPE_KEY).
	get isV4Scoped() : boolean {
		return this.isV4Native && !!localStorage.getItem('v4ScopedAccount');
	}
	// V4 rules per account uuid — see utils/v4Permissions.js. Null on V3 sessions
	// and when the bootstrap couldn't resolve them; both checks below grant what
	// the rules don't cover, so neither can take a capability away.
	get hasV4Permissions() : boolean {
		return !!this.get('v4Permissions');
	}
	get canManageV4Translations() : boolean {
		return canEditV4Translations(this.get('v4Permissions'));
	}
	canUpdateV4Locale(localeId) : boolean {
		return canUpdateV4Locale(this.get('v4Permissions'), localeId);
	}
	canManageV4Locale(localeId) : boolean {
		return canManageV4Locale(this.get('v4Permissions'), localeId);
	}
	canManageV4Devices(accountUuid) : boolean {
		return canManageV4Devices(this.get('v4Permissions'), accountUuid);
	}
	canUpdateV4Account(accountUuid) : boolean {
		return canUpdateV4Account(this.get('v4Permissions'), accountUuid);
	}
	canArchiveV4Account(ownerUuid, accountUuid, aboveUuids) : boolean {
		return canArchiveV4Account(this.get('v4Permissions'), ownerUuid, accountUuid, aboveUuids);
	}
	// `manage.administration` in the session's own (top) account.
	get canManageV4Billing() : boolean {
		return this.isV4Native && canManageV4Billing(this.get('v4Permissions'), this.get('v4OwnerId'));
	}
	/**
	 * Whether a V4 subject (utils/v4Permissions.js V4Subject) may be managed in
	 * the session's OWN account (admin pages use {@link canManageV4Here}), never the
	 * sub-account a manager happened to enter. Grants on a V3 session, which has
	 * no owner uuid: these gates may only ever hide V4-native menu entries.
	 */
	canManageV4(subject) : boolean {
		return canManageV4Subject(this.get('v4Permissions'), this.get('v4OwnerId'), subject);
	}
	/**
	 * {@link canManageV4} asked about a named account rather than the session's
	 * own — what an entered sub-account needs. Grants by default too.
	 */
	canManageV4In(subject, accountUuid) : boolean {
		return canManageV4Subject(this.get('v4Permissions'), accountUuid, subject);
	}
	/**
	 * Whether `accountId` is the account this V4 session itself belongs to — the
	 * manager, not one of its customers. V3 marks that account with the
	 * AccountManager type; V4 lists it like any other, so lists filter on this.
	 */
	isOwnV4Account(accountId) : boolean {
		const own = this.get('v4OwnerId');
		return !!own && !!accountId && String(accountId).toLowerCase() === String(own).toLowerCase();
	}
	// The V4 account this session acts in: the sub-account a manager entered,
	// else its own. Null on V3, which addresses accounts by numeric id.
	get v4ScopedAccountId() : ?string {
		if (!this.isV4Native) return null;
		return getV4SessionScope()?.accountId || this.get('v4OwnerId') || null;
	}
	get isV4ManagerScoped() : boolean {
		return this.isV4Native && this.isAccountManager && !!bootedV4Scope()?.accountId;
	}
	get v4RootAccountId() : ?string {
		if (!this.isV4Native) return null;
		if (this.isV4ManagerScoped) return bootedV4Scope().accountId;
		return this.get('v4OwnerId') || null;
	}
	// Differs from isOwnV4Account inside an entered manager.
	isV4RootAccount(accountId) : boolean {
		const root = this.v4RootAccountId;
		return !!root && !!accountId && String(accountId).toLowerCase() === String(root).toLowerCase();
	}
	canManageV4Here(subject) : boolean {
		return this.isV4ManagerScoped
			? this.canManageV4In(subject, this.v4RootAccountId)
			: this.canManageV4(subject);
	}
	// Whether this session manages the sub-account it entered — the V4 shape of a
	// V3 shadow login (see managesV4Account). Never its own account: a customer's
	// Full-access user holds `*` there too.
	get managesCurrentV4Account() : boolean {
		if (!this.isV4Native || this.isAccountManager) return false;
		const entered = getV4SessionScope()?.accountId;
		if (!entered || this.isOwnV4Account(entered)) return false;
		return managesV4Account(this.get('v4Permissions'), entered);
	}
	// Whether the account may be previewed as its own users see it: the menu's
	// presentation toggle, and the DevCon visibility eyes behind it.
	get canPresentAsCustomer() : boolean {
		return this.isShadow || this.managesCurrentV4Account;
	}
	get countryCode() {
		return this.language.split('-')[1].lc() || 'en';
	}
	get isSuperAdmin() : boolean {
		return [UserTypes.VEVIGO].indexOf(this.get('userType')) >= 0;
	}
	get isAdmin() : boolean {
		if(this.isShadowAdmin) return true;
		return [UserTypes.ADMIN, UserTypes.VEVIGO].indexOf(this.get('userType')) >= 0 || this.get('groupId') === 3;
	}
	get isAccountManager() : boolean {
		if(this.isShadowAccountManager) return true;
		return [UserTypes.ACCOUNT_MANAGER].indexOf(this.get('userType')) >= 0 || this.get('groupId') === 6;
	}
	get isUser() : boolean {
		if(this.isShadowAdmin) return true;
		return [UserTypes.NORMAL, UserTypes.ADMIN].indexOf(this.get('userType')) >= 0;
	}
	get isPortalUser() : boolean {
		return [UserTypes.NORMAL].indexOf(this.get('userType')) >= 0 || this.get('groupId') === 4;
	}
	get isAppOnly() : boolean {
		return [UserTypes.APP_ONLY].indexOf(this.get('userType')) >= 0 || this.get('groupId') === 11;
	}
	get isAlarmCenter() : boolean {
		return this.isARC || this.isMonitorUser;
	}
	get isARC() : boolean {
		return this.get('userType') === UserTypes.ARC;
	}
	// V4 alarm centre bound to one ARC by its rules — its alarms come from that
	// ARC's list, not from an account's.
	get isV4Arc() : boolean {
		return this.isARC && !!this.get('v4ArcId');
	}
	// Synthetic session for /alarm/{b64} V4 public links — has no real JWT.
	get isV4Public() : boolean {
		return this.get('arcKey') === 'v4-public';
	}
	get isPresentation() : boolean {
		return this.get('groupId') === 10;
	}
	get isHidden() : boolean {
		return this.isShadow || this.isPresentation;
	}
	get isShadow() : boolean {
		return this.isShadowAdmin || this.isShadowAccountManager;
	}
	get isShadowAdmin() : boolean {
		return this.get('groupId') === 13;
	}
	get isShadowAccountManager() : boolean {
		return this.get('groupId') === 14;
	}
	get isMonitorUser() : boolean {
		return this.get('groupId') === 8;
	}
	// V4-native sessions enter accounts through v4ScopedAccount (isV4Scoped); a V3 key there is a leftover.
	get viaAdmin() : boolean {
		return !this.isSuperAdmin && !this.isV4Native && (!!localStorage.getItem('adminUser') || !!localStorage.getItem('accountManagerUser'));
	}
	get topAccount() : ?{id: number, name: string} {
		try {
			const raw = localStorage.getItem('accountManagerUser') || localStorage.getItem('adminUser');
			if (!raw) return null;
			const parsed = JSON.parse(raw);
			if (!parsed?.accountId) return null;
			return {id: parsed.accountId, name: parsed.accountName || ''};
		} catch (_) {
			return null;
		}
	}
	get showCredits() : boolean {
		return this.hasModule(moduleIds.Credits);
	}
	get trialEnded() : boolean {
		return this.trialEnd && (this.trialEnd * 1000) < Date.now();
	}
	get hasAlarmCenter(): boolean {
		return !!this.arcKey;
	}
	get mapCenter() {
		return getCentroid(timezoneBoxes[this.timezone] || [[0, 0], [0, 0]]);
	}
	filterString(t) {
		return [
			this.email,
			t(`userGroup.${this.groupId}`)
		].join('').lc();
	}

	canManageModule(moduleId) {
		if(this.isSuperAdmin) return true;
		if(this.isAccountManager) {
			if(moduleId === moduleIds.EchoService) return this.hasModule(moduleIds.EchoService_AM)
			return accountManagerModules.indexOf(moduleId) >= 0;
		}
		return false;
	}

	hasModule(modId) : boolean {
		if(this.isAccountManager) {
			if (modId === moduleIds.Beacons) return true;
		}
		return this.get('modules').indexOf(+modId) >= 0;
	}

	// A V4-native session holds no V3 _AM modules. Not gated on a V4 permission
	// subject: its string is unconfirmed and a wrong one would disable the controls.
	get canEditBeacons() : boolean {
		return this.isSuperAdmin || this.isV4Native || this.hasModule(moduleIds.EditBeacons_AM);
	}

	get canEditSites() : boolean {
		return this.isSuperAdmin || this.isV4Native || this.hasModule(moduleIds.EditSites_AM);
	}

	get trialEndDate() {
		if(this.get('trialEnd'))
			return dayjs.unix(this.get('trialEnd'));
		return null;
	}
	
	clone() {
		return this.set('name', this.name);
	}

	constructor(props) {
		if(!props.userType) {
			props.userType = UserTypes.fromUser(props);
		}
		super(props);
	}

	static create(language = 'en-GB') {
		return new User({
			language,
			groupId: 4,
			email: '',
			userType: UserTypes.NORMAL
		});
	}
}
